import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@frontend/shadcn/components/ui/button';
import { TAutomation, useListAutomationsQuery, useGetAutomationSummaryQuery, useListExecutions, useGetMembersByIdsQuery } from '@frontend/app/hooks';
import { OrderingDirection } from '@frontend/app/types/globalTypes';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { Empty } from '@frontend/app/refresh-components';
import { BoltIcon, PlusIcon } from '@revfluence/fresh-icons/solid/esm';
import { SectionCard } from '../../CampaignPage/components/SectionCard';
import { AutomationItem } from './components/AutomationItem';
import TemplateModal from '../templateModal/TemplateModal';
import { AutomationSummary } from './components/AutomationSummary';
import { getMemberIdFromAutomationExecution, useAutomationExecutionsTableAdapter } from './utils/automationUtils';

// Types
interface AllAutomationsProps {
  projectId: number;
}

// Constants
const AUTOMATIONS_LIMIT = 10;
const EXECUTIONS_LIMIT = 5;
const SKELETON_ITEMS = 3;

// Custom Hooks
const useAutomationsList = (projectId: number) => {
  const {
    data: { listAutomations: { results: automations } } = {
      listAutomations: {
        results: [] as TAutomation[],
      },
    },
    loading: isLoadingAutomations,
    refetch: refetchAutomations,
  } = useListAutomationsQuery({
    variables: {
      paging: {
        offset: 0,
        limit: AUTOMATIONS_LIMIT,
      },
      filters: {
        program: {
          id: projectId,
        },
      },
      ordering: {
        name: OrderingDirection.ASC,
      },
    },
    fetchPolicy: 'network-only',
  });

  return { automations, isLoadingAutomations, refetchAutomations };
};

const useAutomationSummary = (projectId: number) => {
  const {
    data: { getAutomationSummary: summary } = {
      getAutomationSummary: {
        automations: {
          total: 0,
          active: 0,
        },
        executions: {
          pastWeek: {
            total: 0,
          },
          allTime: {
            total: 0,
            failed: 0
          }
        },
      },
    },
    refetch: refetchSummary,
    loading: isLoadingSummary,
  } = useGetAutomationSummaryQuery({
    variables: {
      context: { programId: projectId },
    },
    fetchPolicy: 'no-cache',
  });

  return { summary, isLoadingSummary, refetchSummary };
};

const useAutomationExecutions = (projectId: number) => {
  const {
    data: { listExecutions: { results: executions = [] } } = {
      listExecutions: {
        results: [],
      },
    },
    refetch: refetchExecutions,
    loading: isLoadingExecutions,
  } = useListExecutions({
    variables: {
      paging: {
        offset: 0,
        limit: EXECUTIONS_LIMIT,
      },
      filters: {
        automation: {
          program: {
            id: projectId,
          },
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const memberIds = executions.map(getMemberIdFromAutomationExecution);
  const {
    loading: loadingMembersByIds,
    data: { members: membersByIds = [] } = {},
  } = useGetMembersByIdsQuery(memberIds, {
    skip: !memberIds.length,
    fetchPolicy: 'no-cache',
  });

  const automationExecutions = useAutomationExecutionsTableAdapter({
    executions,
    members: membersByIds
  });

  return {
    executions: automationExecutions,
    isLoading: isLoadingExecutions || loadingMembersByIds,
    refetchExecutions
  };
};

const useTemplateModal = () => {
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);

  const closeTemplateModal = useCallback(() => {
    setIsTemplateModalVisible(false);
  }, []);

  const openTemplateModal = useCallback(() => {
    setIsTemplateModalVisible(true);
  }, []);

  return {
    isTemplateModalVisible,
    closeTemplateModal,
    openTemplateModal,
  };
};

const LoadingState: React.FC = () => (
  <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-gray-50 overflow-hidden">
    {/* Left Section - Form */}
    <div className="w-full lg:w-[70%] overflow-hidden p-6 flex flex-col gap-2">
      <Skeleton className="h-7 w-32" />
      <div className="flex-1 w-[700px]">
        <div className="bg-white rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="space-y-2">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-4 w-48" />
            </div>
            <Skeleton className="h-10 w-32" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: SKELETON_ITEMS }).map((_, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Divider */}
    <div className="hidden lg:block w-px bg-gray-200" />

    {/* Right Section - Summary */}
    <div className="w-full lg:w-[30%] overflow-hidden p-6">
      <div className="space-y-4 w-[375px] mx-auto">
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-6 rounded-lg" />
          <Skeleton className="h-6 w-32" />
        </div>
        <div className="bg-white rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="flex flex-col gap-2 items-center">
                <Skeleton className="h-6 w-12" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
          <Skeleton className="h-px w-full my-3" />
          <div className="space-y-3">
            <Skeleton className="h-5 w-32" />
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AutomationList: React.FC<{
  automations: TAutomation[];
  projectId: number;
  refetchAutomations: () => void;
}> = ({ automations, projectId, refetchAutomations }) => (
  <div className="flex flex-wrap gap-4">
    {automations.map((automation) => (
      <AutomationItem
        key={automation.id}
        automation={automation}
        projectId={projectId}
        refetchAutomations={refetchAutomations}
      />
    ))}
  </div>
);

const AddAutomationButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button onClick={onClick} className="flex items-center gap-2 self-start mt-0.5" variant="outline" size="xs">
    <FontAwesomeIcon icon={faPlus} />
    Automation
  </Button>
);

const AutomationContent: React.FC<{
  automations: TAutomation[];
  projectId: number;
  refetchAutomations: () => void;
  onAddAutomation: () => void;
}> = ({ automations, projectId, refetchAutomations, onAddAutomation }) => (
  <SectionCard
    title="All Automations"
    description="Automate project tasks to easily scale your campaigns. Add a new automation or configure an existing one."
    rightContent={<AddAutomationButton onClick={onAddAutomation} />}
  >
    {automations.length > 0 ? (
      <AutomationList
        automations={automations}
        projectId={projectId}
        refetchAutomations={refetchAutomations}
      />
    ) : (
      <Empty
        title="Add Your First Automation"
        description="Automations enable streamlined collaborations."
        icon={BoltIcon}
        primaryCtaProps={{
          onClick: onAddAutomation,
          text: 'Automation',
          icon: PlusIcon,
        }}
        size="lg"
        layout="horizontal"
        withBackground
      />

    )}
  </SectionCard>
);

// Main Component
export const AllAutomations: React.FC<AllAutomationsProps> = ({ projectId }) => {
  const { automations, isLoadingAutomations, refetchAutomations } = useAutomationsList(projectId);
  const { summary, isLoadingSummary, refetchSummary } = useAutomationSummary(projectId);
  const { executions, isLoading: isLoadingExecutions, refetchExecutions } = useAutomationExecutions(projectId);
  const { isTemplateModalVisible, closeTemplateModal, openTemplateModal } = useTemplateModal();

  const handleRefetch = useCallback(async () => {
    await Promise.all([
      refetchAutomations(),
      refetchSummary(),
      refetchExecutions()
    ]);
  }, [refetchAutomations, refetchSummary, refetchExecutions]);

  if (isLoadingAutomations) {
    return <LoadingState />;
  }

  return (
    <>
      <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-gray-50 overflow-hidden">
        {/* Left Section - Form */}
        <div className="w-full lg:w-[70%] overflow-hidden p-6 flex flex-col gap-2">
          <H3 className="m-0 font-medium text-lg">Automations</H3>
          <AutomationContent
            automations={automations}
            projectId={projectId}
            refetchAutomations={handleRefetch}
            onAddAutomation={openTemplateModal}
          />
        </div>

        {/* Divider */}
        <div className="hidden lg:block w-px bg-grey-2" />

        {/* Right Section - Campaign Snapshot */}
        <div className="w-full lg:w-[30%] overflow-hidden p-6">
          <AutomationSummary
            summary={summary}
            executions={executions}
            isLoading={isLoadingSummary || isLoadingExecutions}
          />
        </div>
      </div>
      <TemplateModal
        projectId={projectId}
        isVisible={isTemplateModalVisible}
        onCancel={closeTemplateModal}
      />
    </>
  );
};
